import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormData } from './types';
import { step1Schema, step2Schema, INITIAL_FORM_DATA } from './constants';
import { checkClientEmail, checkClientPhone } from '@/lib/apiService';
import {
  getDialCodeForCountryCode,
  getPhoneCountryFromLocation,
} from '../../../utils/country-phone-mapping';
import { calculateAgeFromDateString } from './date-utils';

const PHONE_TAKEN_MESSAGE = 'Phone number is already taken.';
const EMAIL_TAKEN_MESSAGE = 'Email address is already taken.';

export const useAgeCalculation = (dateOfBirth: string) => {
  const [age, setAge] = useState(0);

  useEffect(() => {
    const calculatedAge = calculateAgeFromDateString(dateOfBirth);
    setAge(calculatedAge ?? 0);
  }, [dateOfBirth]);

  return age;
};

interface UseClientFormOptions {
  excludeClientId?: string;
}

export const useClientForm = (options: UseClientFormOptions = {}) => {
  const { excludeClientId } = options;
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState('in');

  const step1Form = useForm({
    resolver: zodResolver(step1Schema),
    defaultValues: formData,
  });

  const step2Form = useForm({
    resolver: zodResolver(step2Schema),
    defaultValues: formData,
  });

  const phoneMappingRequestIdRef = useRef(0);
  const emailCheckRequestIdRef = useRef(0);
  const emailCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getEffectiveGenderForPregnancy = useCallback((data: FormData): string => {
    if (data.gender === 'male' || data.gender === 'female') return data.gender;
    if (data.sexAtBirth === 'male' || data.sexAtBirth === 'female') return data.sexAtBirth;
    return '';
  }, []);

  const normalizeDialCode = useCallback((value: string): string => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';
    return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
  }, []);

  const clearEmailTakenError = useCallback(() => {
    if (step1Form.getFieldState('email').error?.message === EMAIL_TAKEN_MESSAGE) {
      step1Form.clearErrors('email');
    }
  }, [step1Form]);

  const syncPhoneCountryMapping = useCallback(
    async (phone: string, phoneCountryCode: string, shouldApplyTakenValidation: boolean) => {
      const normalizedPhone = String(phone || '').trim();
      const normalizedDialCode = normalizeDialCode(phoneCountryCode);
      if (!normalizedPhone || !normalizedDialCode) return;

      const requestId = ++phoneMappingRequestIdRef.current;
      try {
        const mapping = await checkClientPhone({
          phone: normalizedPhone,
          phoneCountryCode: normalizedDialCode,
          ...(excludeClientId ? { excludeClientId } : {}),
        });
        if (requestId !== phoneMappingRequestIdRef.current) return;

        const resolvedDialCode = normalizeDialCode(mapping.phoneCountryCode || normalizedDialCode);
        const resolvedCountryCode = String(mapping.countryCode || '').trim().toLowerCase();
        const resolvedLocation = String(mapping.location || '').trim();
        const isTaken = mapping.isTaken === true || mapping.isUnique === false;

        if (currentStep === 1 && shouldApplyTakenValidation) {
          if (isTaken) {
            step1Form.setError('phone', {
              type: 'manual',
              message: PHONE_TAKEN_MESSAGE,
            });
          } else if (step1Form.formState.errors.phone?.message === PHONE_TAKEN_MESSAGE) {
            step1Form.clearErrors('phone');
          }
        }

        if (resolvedCountryCode) {
          setPhoneCountry(resolvedCountryCode);
        }

        setFormData((prev) => ({
          ...prev,
          phone_country_code: resolvedDialCode,
          ...(resolvedLocation ? { location: resolvedLocation } : {}),
        }));

        if (currentStep === 1) {
          step1Form.setValue('phone_country_code', resolvedDialCode, { shouldValidate: false });
          if (resolvedLocation) {
            step1Form.setValue('location', resolvedLocation, { shouldValidate: false });
          }
        }
      } catch (error) {
        console.error('Error checking phone mapping:', error);
      }
    },
    [currentStep, excludeClientId, normalizeDialCode, step1Form]
  );

  useEffect(() => {
    if (currentStep !== 1) return;

    const normalizedEmail = String(formData.email || '').trim();
    if (emailCheckTimeoutRef.current) {
      clearTimeout(emailCheckTimeoutRef.current);
      emailCheckTimeoutRef.current = null;
    }

    if (!normalizedEmail) {
      clearEmailTakenError();
      return;
    }

    const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
    if (!emailIsValid) {
      clearEmailTakenError();
      return;
    }

    const requestId = ++emailCheckRequestIdRef.current;
    emailCheckTimeoutRef.current = setTimeout(() => {
      void (async () => {
        try {
          const result = await checkClientEmail({
            email: normalizedEmail,
            ...(excludeClientId ? { excludeClientId } : {}),
          });

          if (requestId !== emailCheckRequestIdRef.current) return;

          const isTaken = result.isTaken === true || result.isUnique === false || result.exists === true;
          if (isTaken) {
            step1Form.setError('email', {
              type: 'manual',
              message: EMAIL_TAKEN_MESSAGE,
            });
            return;
          }

          clearEmailTakenError();
        } catch (error) {
          console.error('Error checking email availability:', error);
        }
      })();
    }, 320);

    return () => {
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
        emailCheckTimeoutRef.current = null;
      }
    };
  }, [currentStep, formData.email, excludeClientId, step1Form, clearEmailTakenError]);

  const handleInputChange = useCallback((field: keyof FormData, value: string) => {
    if (field === 'preferredMeasurementSystem') {
      setFormData(prev => ({ ...prev, [field]: value as any }));
      step1Form.setValue(field, value as any, { shouldValidate: currentStep === 1 });
      step2Form.setValue(field, value as any, { shouldValidate: false });
      return;
    }

      // Special handling for formulaId and drvFormulaId - keep as string since backend expects string
      if (field === 'formulaId' || field === 'drvFormulaId') {
        setFormData(prev => ({ ...prev, [field]: value as any }));
        if (currentStep === 2) {
          step2Form.setValue(field, value, { shouldValidate: true });
        }
        return;
      }
    
    // Pregnancy/lactation logic: gender first; if not male/female, fallback to sexAtBirth.
    if (field === 'gender') {
      const isNewGenderSet = value === 'male' || value === 'female';
      const newEffectiveGender = isNewGenderSet ? value : formData.sexAtBirth;
      const isFemale = newEffectiveGender === 'female';

      setFormData(prev => {
        const newData = { ...prev, [field]: value };
        
        // If effective gender is not female, clear pregnancy/lactation fields
        if (!isFemale) {
          newData.pregnancyStatus = '';
          newData.pregnancyTrimester = '';
          newData.prePregnancyWeightKg = '';
          newData.lactationStatus = '';
          newData.twinPregnancy = '';
          newData.weightLossGoal = '';
        }
        
        // If effective gender is female and age < 18, clear pregnancy
        if (isFemale && prev.dateOfBirth) {
          const calculatedAge = calculateAgeFromDateString(prev.dateOfBirth);
          if (calculatedAge !== null && calculatedAge < 18) {
            newData.pregnancyStatus = '';
            newData.pregnancyTrimester = '';
            newData.prePregnancyWeightKg = '';
            newData.lactationStatus = '';
            newData.twinPregnancy = '';
            newData.weightLossGoal = '';
          }
        }
        
        return newData;
      });

      step1Form.setValue('gender', value as any);
      step2Form.setValue('gender', value as any);

      if (!isFemale) {
        step2Form.setValue('pregnancyStatus', '');
        step2Form.setValue('pregnancyTrimester', '');
        step2Form.setValue('prePregnancyWeightKg', '');
        step2Form.setValue('lactationStatus', '');
        step2Form.setValue('twinPregnancy', '');
        step2Form.setValue('weightLossGoal', '');
        step2Form.clearErrors(['pregnancyTrimester', 'prePregnancyWeightKg', 'lactationStatus', 'twinPregnancy', 'weightLossGoal']);
      } else {
        const calculatedAge = calculateAgeFromDateString(formData.dateOfBirth);
        if (calculatedAge !== null && calculatedAge < 18) {
          step2Form.setValue('pregnancyStatus', '');
          step2Form.setValue('pregnancyTrimester', '');
          step2Form.setValue('prePregnancyWeightKg', '');
          step2Form.setValue('lactationStatus', '');
          step2Form.setValue('twinPregnancy', '');
          step2Form.setValue('weightLossGoal', '');
          step2Form.clearErrors(['pregnancyTrimester', 'prePregnancyWeightKg', 'lactationStatus', 'twinPregnancy', 'weightLossGoal']);
        }
      }
      return;
    }
    
    // Sex at birth affects pregnancy/lactation only when gender is not male/female.
    if (field === 'sexAtBirth') {
      const isGenderSet = formData.gender === 'male' || formData.gender === 'female';
      const newEffectiveGender = isGenderSet ? formData.gender : value;
      const isFemale = newEffectiveGender === 'female';

      setFormData(prev => {
        const newData = { ...prev, [field]: value };
        
        // If effective gender is not female, clear pregnancy/lactation fields
        if (!isFemale) {
          newData.pregnancyStatus = '';
          newData.pregnancyTrimester = '';
          newData.prePregnancyWeightKg = '';
          newData.lactationStatus = '';
          newData.twinPregnancy = '';
          newData.weightLossGoal = '';
        }
        
        // If effective gender is female and age < 18, clear pregnancy
        if (isFemale && prev.dateOfBirth) {
          const calculatedAge = calculateAgeFromDateString(prev.dateOfBirth);
          if (calculatedAge !== null && calculatedAge < 18) {
            newData.pregnancyStatus = '';
            newData.pregnancyTrimester = '';
            newData.prePregnancyWeightKg = '';
            newData.lactationStatus = '';
            newData.twinPregnancy = '';
            newData.weightLossGoal = '';
          }
        }
        
        return newData;
      });
      
      step1Form.setValue('sexAtBirth', value as any);
      step2Form.setValue('sexAtBirth', value as any);

      if (!isFemale) {
        step2Form.setValue('pregnancyStatus', '');
        step2Form.setValue('pregnancyTrimester', '');
        step2Form.setValue('prePregnancyWeightKg', '');
        step2Form.setValue('lactationStatus', '');
        step2Form.setValue('twinPregnancy', '');
        step2Form.setValue('weightLossGoal', '');
        step2Form.clearErrors(['pregnancyTrimester', 'prePregnancyWeightKg', 'lactationStatus', 'twinPregnancy', 'weightLossGoal']);
      } else {
        const calculatedAge = calculateAgeFromDateString(formData.dateOfBirth);
        if (calculatedAge !== null && calculatedAge < 18) {
          step2Form.setValue('pregnancyStatus', '');
          step2Form.setValue('pregnancyTrimester', '');
          step2Form.setValue('prePregnancyWeightKg', '');
          step2Form.setValue('lactationStatus', '');
          step2Form.setValue('twinPregnancy', '');
          step2Form.setValue('weightLossGoal', '');
          step2Form.clearErrors(['pregnancyTrimester', 'prePregnancyWeightKg', 'lactationStatus', 'twinPregnancy', 'weightLossGoal']);
        }
      }
      return; 
    }

    if (field === 'dateOfBirth') {
      const calculatedAge = calculateAgeFromDateString(value);
      const ageValue = calculatedAge !== null ? String(calculatedAge) : '';

      setFormData((prev) => {
        const updatedData = {
          ...prev,
          dateOfBirth: value,
          age: ageValue,
        };

        const effectiveGenderForPregnancy = getEffectiveGenderForPregnancy(updatedData);
        const shouldClearPregnancyAndLactation =
          effectiveGenderForPregnancy === 'female' && calculatedAge !== null && calculatedAge < 18;

        if (shouldClearPregnancyAndLactation) {
          updatedData.pregnancyStatus = '';
          updatedData.pregnancyTrimester = '';
          updatedData.prePregnancyWeightKg = '';
          updatedData.lactationStatus = '';
          updatedData.twinPregnancy = '';
          updatedData.weightLossGoal = '';
        }

        return updatedData;
      });

      step1Form.setValue('dateOfBirth', value);
      step1Form.setValue('age', ageValue, { shouldValidate: false });
      step2Form.setValue('dateOfBirth', value, { shouldValidate: currentStep === 2 });

      const effectiveGenderForPregnancy = getEffectiveGenderForPregnancy({
        ...formData,
        dateOfBirth: value,
        age: ageValue,
      });
      const shouldClearPregnancyAndLactation =
        effectiveGenderForPregnancy === 'female' && calculatedAge !== null && calculatedAge < 18;

      if (shouldClearPregnancyAndLactation) {
        step2Form.setValue('pregnancyStatus', '');
        step2Form.setValue('pregnancyTrimester', '');
        step2Form.setValue('prePregnancyWeightKg', '');
        step2Form.setValue('lactationStatus', '');
        step2Form.setValue('twinPregnancy', '');
        step2Form.setValue('weightLossGoal', '');
        step2Form.clearErrors(['pregnancyTrimester', 'prePregnancyWeightKg', 'lactationStatus', 'twinPregnancy', 'weightLossGoal']);
      }

      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Reset fields when switching between Pregnant and Not Pregnant
    if (field === 'pregnancyStatus') {
      const effectiveGenderForPregnancy = getEffectiveGenderForPregnancy(formData);
      // Prevent setting pregnancy status for females below 18
      if (effectiveGenderForPregnancy === 'female' && formData.dateOfBirth && value === 'pregnant') {
        const calculatedAge = calculateAgeFromDateString(formData.dateOfBirth);
        if (calculatedAge !== null && calculatedAge < 18) {
          // Don't allow setting to pregnant for females below 18
          return;
        }
      }
      
      if (value === 'none' || value === 'not_pregnant') {
        const currentIsLactating = Boolean(formData.lactationStatus) && formData.lactationStatus !== 'none';
        setFormData(prev => ({
          ...prev,
          pregnancyTrimester: '',
          prePregnancyWeightKg: '',
          ...(!currentIsLactating ? { twinPregnancy: '', weightLossGoal: '' } : {})
        }));
        step2Form.setValue('pregnancyTrimester', '');
        step2Form.setValue('prePregnancyWeightKg', '');
        if (!currentIsLactating) {
          step2Form.setValue('twinPregnancy', '');
          step2Form.setValue('weightLossGoal', '');
        }
      } else if (value === 'pregnant') {
        const currentIsLactating = Boolean(formData.lactationStatus) && formData.lactationStatus !== 'none';
        if (!currentIsLactating) {
          setFormData(prev => ({
            ...prev,
            weightLossGoal: ''
          }));
          step2Form.setValue('weightLossGoal', '');
        }
      }
    }
    
    // Reset fields when switching lactation status
    if (field === 'lactationStatus') {
      const nextIsLactating = Boolean(value) && value !== 'none';
      const wasLactating = Boolean(formData.lactationStatus) && formData.lactationStatus !== 'none';
      const isPregnant = formData.pregnancyStatus === 'pregnant' ||
        formData.pregnancyStatus === 'first' ||
        formData.pregnancyStatus === 'second' ||
        formData.pregnancyStatus === 'third';

      if (!nextIsLactating) {
        setFormData(prev => ({
          ...prev,
          ...(!isPregnant ? { twinPregnancy: '' } : {}),
          weightLossGoal: ''
        }));
        if (!isPregnant) {
          step2Form.setValue('twinPregnancy', '');
        }
        step2Form.setValue('weightLossGoal', '');
      } else if (!wasLactating) {
        // Switching from non-lactating to lactating: force selecting lactation goal explicitly
        setFormData(prev => ({
          ...prev,
          weightLossGoal: ''
        }));
        step2Form.setValue('weightLossGoal', '');
      }
    }
    
    // Auto-update phone country code when location (country) changes
    if (field === 'location' && value) {
      const phoneCode = getPhoneCountryFromLocation(value);
      if (phoneCode) {
        setPhoneCountry(phoneCode);
        const nextDialCode = `+${getDialCodeForCountryCode(phoneCode)}`;
        setFormData(prev => ({ ...prev, phone_country_code: nextDialCode }));
        if (currentStep === 1) {
          step1Form.setValue('phone_country_code', nextDialCode, { shouldValidate: false });
        }
        if (formData.phone.trim()) {
          void syncPhoneCountryMapping(formData.phone, nextDialCode, true);
        } else {
          phoneMappingRequestIdRef.current += 1;
        }
      }
    }
    
    if (currentStep === 1) {
      step1Form.setValue(field as any, value);
    } else if (currentStep === 2) {
      // For pregnancy and lactation-related fields, trigger validation to show errors immediately
      const shouldValidate = field === 'pregnancyStatus' || 
                            field === 'pregnancyTrimester' || 
                            field === 'prePregnancyWeightKg' ||
                            field === 'weightKg' ||
                            field === 'heightCm' ||
                            field === 'twinPregnancy' ||
                            field === 'lactationStatus' ||
                            field === 'sexAtBirth' ||
                            field === 'weightLossGoal';
      step2Form.setValue(field as any, value, { shouldValidate });
    }
  }, [currentStep, formData, getEffectiveGenderForPregnancy, step1Form, step2Form, syncPhoneCountryMapping]);

  const handlePhoneChange = (value: string, data: any) => {
    let phonePart = value;
    if (data.dialCode && value.startsWith(data.dialCode)) {
      phonePart = value.slice(data.dialCode.length);
    } else if (data.dialCode && value.startsWith('+' + data.dialCode)) {
      phonePart = value.slice(('+' + data.dialCode).length);
    }
    const nextDialCode = `+${data.dialCode || ''}`;
    const nextPhoneCountry = typeof data.countryCode === 'string' && data.countryCode.trim()
      ? data.countryCode.toLowerCase()
      : phoneCountry;

    setPhoneCountry(nextPhoneCountry);
    setFormData(prev => ({
      ...prev,
      phone: phonePart,
      phone_country_code: nextDialCode,
    }));
    if (currentStep === 1) {
      const shouldValidatePhone = Boolean(step1Form.getFieldState('phone').error);
      step1Form.setValue('phone', phonePart, { shouldDirty: true, shouldValidate: shouldValidatePhone });
      step1Form.setValue('phone_country_code', nextDialCode, {
        shouldDirty: true,
        shouldValidate: shouldValidatePhone,
      });
      if (!phonePart.trim() && step1Form.formState.errors.phone?.message === PHONE_TAKEN_MESSAGE) {
        step1Form.clearErrors('phone');
      }
    }

    if (phonePart.trim()) {
      void syncPhoneCountryMapping(phonePart, nextDialCode, true);
    } else {
      phoneMappingRequestIdRef.current += 1;
    }
  };





  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setCurrentStep(1);
    step1Form.reset();
    step2Form.reset();
  };

  return {
    formData,
    setFormData,
    currentStep,
    setCurrentStep,
    isSaving,
    setIsSaving,
    step1Form,
    step2Form,
    handleInputChange,
    handlePhoneChange,
    resetForm,
    phoneCountry,
    setPhoneCountry,
  };
}; 
